import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface TabItem {
  id: string;
  name: string;
  icon?: string;
}

interface TabsProps {
  tabs: TabItem[];
  selectedTab: string;
  onTabPress: (tabId: string, event?: unknown) => void;
  variant?: 'default' | 'underline' | 'compact';
  /** Smaller padding and font for mobile / single row */
  size?: 'default' | 'dense';
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  selectedTab,
  onTabPress,
  variant = 'default',
  size = 'default',
}) => {
  const { colors } = useTheme();
  const [hoveredTabId, setHoveredTabId] = useState<string | null>(null);
  const isDense = size === 'dense';
  const iconSize = isDense ? 14 : 16;

  const renderTab = (tab: TabItem) => {
    const isSelected = selectedTab === tab.id;
    const isHovered = hoveredTabId === tab.id;
    const usePrimaryColor = isSelected || isHovered;

    if (variant === 'compact') {
      return (
        <Pressable
          key={tab.id}
          style={({ pressed }) => [
            styles.compactTab,
            isDense && styles.compactTabDense,
            { backgroundColor: 'transparent' },
            isHovered && { backgroundColor: colors.primary + '12' },
            pressed && { backgroundColor: colors.primary + '1A' },
          ]}
          onPress={(e) => onTabPress(tab.id, e)}
          onHoverIn={() => setHoveredTabId(tab.id)}
          onHoverOut={() => setHoveredTabId(null)}
        >
          {tab.icon && (
            <Ionicons
              name={tab.icon as React.ComponentProps<typeof Ionicons>['name']}
              size={iconSize}
              color={usePrimaryColor ? colors.primary : colors.secondary}
              style={styles.tabIcon}
            />
          )}
          <Text
            style={[
              styles.compactTabText,
              isDense && styles.compactTabTextDense,
              {
                color: usePrimaryColor ? colors.primary : colors.text,
              },
            ]}
            numberOfLines={1}
          >
            {tab.name}
          </Text>
        </Pressable>
      );
    }

    if (variant === 'underline') {
      return (
        <Pressable
          key={tab.id}
          style={[
            styles.underlineTab,
            { borderBottomColor: colors.border },
            (isSelected || isHovered) && { borderBottomColor: colors.primary },
            isHovered &&
              !isSelected && { backgroundColor: colors.primary + '12' },
          ]}
          onPress={(e) => onTabPress(tab.id, e)}
          onHoverIn={() => setHoveredTabId(tab.id)}
          onHoverOut={() => setHoveredTabId(null)}
        >
          {tab.icon && (
            <Ionicons
              name={tab.icon as React.ComponentProps<typeof Ionicons>['name']}
              size={16}
              color={usePrimaryColor ? colors.primary : colors.secondary}
              style={styles.tabIcon}
            />
          )}
          <Text
            style={[
              styles.underlineTabText,
              { color: usePrimaryColor ? colors.primary : colors.text },
            ]}
          >
            {tab.name}
          </Text>
        </Pressable>
      );
    }

    // Default variant
    return (
      <Pressable
        key={tab.id}
        style={[
          styles.defaultTab,
          {
            backgroundColor: isSelected ? colors.primary : 'transparent',
            borderColor: colors.border,
          },
          isHovered &&
            !isSelected && {
              backgroundColor: colors.primary + '18',
              borderColor: colors.primary + '40',
            },
        ]}
        onPress={(e) => onTabPress(tab.id, e)}
        onHoverIn={() => setHoveredTabId(tab.id)}
        onHoverOut={() => setHoveredTabId(null)}
      >
        {tab.icon && (
          <Ionicons
            name={tab.icon as React.ComponentProps<typeof Ionicons>['name']}
            size={16}
            color={
              usePrimaryColor
                ? isSelected
                  ? colors.background
                  : colors.primary
                : colors.secondary
            }
            style={styles.tabIcon}
          />
        )}
        <Text
          style={[
            styles.defaultTabText,
            {
              color: usePrimaryColor
                ? isSelected
                  ? colors.background
                  : colors.primary
                : colors.text,
            },
          ]}
        >
          {tab.name}
        </Text>
      </Pressable>
    );
  };

  return (
    <View
      style={[
        styles.container,
        variant === 'underline' && {
          borderBottomColor: colors.border,
          borderBottomWidth: 1,
        },
        variant === 'compact' && styles.compactContainer,
      ]}
    >
      <View
        style={[
          styles.content,
          variant === 'compact' && styles.compactContent,
          isDense && styles.compactContentDense,
        ]}
      >
        {tabs.map(renderTab)}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderBottomWidth: 1,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    flexWrap: 'nowrap',
  },
  // Compact variant styles
  compactContainer: {
    borderBottomWidth: 0,
  },
  compactContent: {
    paddingHorizontal: 0,
    gap: 2,
  },
  compactContentDense: {
    gap: 0,
    paddingHorizontal: 0,
  },
  compactTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    flexShrink: 0,
    justifyContent: 'center',
    minHeight: 36,
  },
  compactTabDense: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    minHeight: 32,
    borderRadius: 6,
  },
  compactTabText: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
  },
  compactTabTextDense: {
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16,
  },
  // Underline variant styles
  underlineTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    flexShrink: 0,
    justifyContent: 'center',
  },
  underlineTabText: {
    fontSize: 14,
    fontWeight: '500',
  },
  // Default variant styles
  defaultTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginRight: 8,
    borderWidth: 1,
  },
  defaultTabText: {
    fontSize: 14,
    fontWeight: '500',
  },
  tabIcon: {
    marginRight: 3,
  },
});

export default Tabs;
